"use client";
import React from "react";
import { PageThankYouSchema } from "@/sanity/pages/PageThankYouTemplate";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import Header from "../global/partials/Header";
import Video from "../global/Video";
import grid from "@/public/global/grid.svg";
import vestraLogo from "@/public/icons/vestra-logo.svg";
import { SanityVideoPlayer } from "../ui/SanityVideoPlayer";
import Footer from "../global/partials/Footer";

export default function PageThankYouJSX({
  data,
}: {
  data: PageThankYouSchema;
}) {
  return (
    <>
      <section className="relative min-h-screen w-full pb-[23rem] lg:pb-0">
        <Image
          src={grid}
          alt="grid"
          className="absolute z-[0] inset-0 !w-full !h-full object-cover"
        />

        {/* Hero Section */}
        <div className=" relative z-[1]">
          <div className="container mx-auto px-[2rem] pt-[6.5rem]">
            <div className="flex flex-col items-center justify-center min-h-[70vh] text-center">
              <Image
                src={vestraLogo}
                alt="logo"
                className="!w-[20rem] mb-[6.5rem]"
              />

              {/* Hero Title */}
              {data.heroTitle && (
                <div
                  className="mb-[2rem] lg:mb-[7.12rem] richtext richtext-center prose-h1:text-[#012234]/90 prose-h1:font-normal prose-h1:w-[min(114rem,100%)]
                  prose-p:text-black/60 prose-p:text-[2rem] lg:prose-p:text-[2.25rem] prose-p:leading-[150%] prose-p:w-[min(85rem,100%)]"
                >
                  <PortableText
                    value={data.heroTitle}
                    components={richTextComponent}
                  />
                </div>
              )}

              {data.heroCTA && (
                <CTAButton
                  data={data.heroCTA}
                  className="lg:hidden mb-[2.75rem]"
                />
              )}

              <div className="w-[min(187.375rem,100%)] grid grid-cols-1 lg:grid-cols-[99.875rem_auto] items-center gap-[3rem] lg:gap-[8rem]">
                <div className="bg-[#71A961]/[0.04] border border-[#71A961]/[0.16] rounded-[0.85rem] lg:rounded-[1.7rem] p-[0.5rem] lg:p-[1rem]">
                  {data.heroVideoToggle ? (
                    data.heroVideo && (
                      <div className="w-full *:w-full aspect-video">
                        <SanityVideoPlayer
                          playerData={data.heroVideo}
                          playsinline={true}
                          className="rounded-[1.25rem]"
                        />
                      </div>
                    )
                  ) : (
                    <Image src={data.heroImage} className="rounded-[1.25rem]" />
                  )}
                </div>

                <div className="w-full flex flex-col items-start gap-[5rem]">
                  {/* Hero List */}
                  {data.heroList &&
                    data.heroList.length > 0 &&
                    data.heroList.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-start justify-center gap-[3rem]"
                      >
                        {item.icon && (
                          <Image
                            src={item.icon}
                            alt={item.icon.name}
                            className="flex-shrink-0 !w-[6.285rem]"
                          />
                        )}
                        {item.text && (
                          <div className="richtext richtext-start prose-p:text-black/60">
                            <PortableText
                              value={item.text}
                              components={richTextComponent}
                            />
                          </div>
                        )}
                      </div>
                    ))}

                  {/* Hero CTA */}
                  {data.heroCTA && (
                    <CTAButton data={data.heroCTA} className="" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {data.settings.footer && <Footer data={data.settings.footer} slug={data?.settings?.slug?.current} />}
    </>
  );
}
